import React, { useEffect, useState } from 'react';
import {
	Breakpoints,
	Container,
	ContainerPadding,
	CTA,
	DisplayTag,
	Layout,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { ChevronRight, Safety } from '@volkswagen-onehub/icons-core';
import { ServiceStatusIndicator } from './service-status-indicator';
import {
	extendedWarrantyEndDate,
	hasContract,
	isWarrantyActive,
	isIDcareActive,
	isLongdriveActive,
	longdriveProduct,
	manufacturerWarrantyEndDate,
} from '../../../helpers/servicesHelper';
import { Vehicle } from '@esportal/portal-service';
import { ServiceType } from '../detail-layer/services-layer-types';
import { AccordionStyled } from './accordion-styled';

const useIsMax959 = (maxWidth = 959): boolean => {
	const [isMax959, setIsMax959] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
		const handleChange = () => setIsMax959(mediaQuery.matches);

		handleChange();
		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [maxWidth]);

	return isMax959;
};

export const ServicesCoberturas = ({
	vehicle,
	isOpen,
	onToggle,
	isPreviousOpen,
	openServicesLayer,
}: {
	vehicle: Vehicle;
	isOpen: boolean;
	onToggle: () => void;
	isPreviousOpen: boolean;
	openServicesLayer: (type: ServiceType) => void;
}): React.ReactElement => {
	const isMax959 = useIsMax959();
	const manufacturerWarrantyDate = manufacturerWarrantyEndDate(
		vehicle.vgedRegistrationDate,
		vehicle.warranty?.vgedYearsWarrantyCoverage,
	);
	const extensionWarrantyDate = extendedWarrantyEndDate(
		vehicle.vgedRegistrationDate,
		vehicle.warranty?.vgedYearsWarrantyCoverage,
		vehicle.warranty?.vgedYearsWarrantyExtension,
	);
	const warrantyDate = extensionWarrantyDate ?? manufacturerWarrantyDate;

	return (
		<AccordionStyled
			icon={<Safety variant="default" />}
			title={`Coberturas de tu ${vehicle.vgedModelGroup}`}
			subtitle="Consulta qué coberturas tienes actualmente y qué otras opciones puedes añadir."
			isOpen={isOpen}
			onToggle={onToggle}
			isPreviousOpen={isPreviousOpen}
		>
			<Container
				wrap="always"
				padding={{
					left: ContainerPadding.grid001,
					right: ContainerPadding.grid001,
				}}
			>
				<Layout
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 24 },
							{ name: '.', columns: 0 },
						],
					}}
				>
					<Text
						appearance={isMax959 ? TokenTextAppearance.copy200 : TokenTextAppearance.bigcopy100}
						textAlign={TextAlignment.left}
					>
						Estas son las coberturas que te ofrecemos para que disfrutes al máximo de tu Volkswagen.
					</Text>
				</Layout>
			</Container>

			<Layout
				appearance={{
					[Breakpoints.default]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'c', columns: 24 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b560]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'c', columns: 24 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b960]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 12 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 12 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b1280]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 12 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 12 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b1600]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 8 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 8 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'c', columns: 8 },
						{ name: '.', columns: 0 },
					],
				}}
			>
				{isLongdriveActive(vehicle) && (
					<Container
						wrap="always"
						padding={{
							top: ContainerPadding.dynamic0150,
							left: ContainerPadding.grid001,
							right: ContainerPadding.grid001,
						}}
					>
						<Text appearance={TokenTextAppearance.headline200} textAlign={TextAlignment.left} bold>
							Volkswagen Long Drive {hasContract(vehicle.longdrive?.contractEndDate) && longdriveProduct(vehicle)}
						</Text>
						{vehicle.longdrive && hasContract(vehicle.longdrive.contractEndDate) ? (
							<ServiceStatusIndicator date={vehicle.longdrive.contractEndDate} fullDescription={false} />
						) : (
							<Container
								padding={{
									top: ContainerPadding.static150,
									bottom: ContainerPadding.static250,
								}}
								verticalAlign="center"
								shrinkContent
							>
								<DisplayTag appearance="new" text="Contrátalo" />
							</Container>
						)}
						<Text appearance={TokenTextAppearance.label200} textAlign={TextAlignment.left}>
							{hasContract(vehicle.longdrive?.contractEndDate)
								? longdriveProduct(vehicle) === 'Standard'
									? 'Tu suscripción incluye el mantenimiento de tu coche.'
									: longdriveProduct(vehicle) === 'Advance'
										? 'Tu suscripción incluye el mantenimiento y cambio de piezas por desgaste de tu coche.'
										: longdriveProduct(vehicle) === 'Pro'
											? 'Tu suscripción incluye el mantenimiento, cambio de piezas por desgaste y neumáticos de tu coche.'
											: 'Esta suscripción incluye todo el mantenimiento de tu coche en una única cuota mensual'
								: 'Si te suscribes a lo que te gusta, ¿por qué no hacerlo al plan de mantenimiento de Volkswagen? Tendrás todo el cuidado de tu coche en una cuota mensual.'}
						</Text>

						<Container
							padding={{
								top: isMax959 ? ContainerPadding.dynamic0050 : ContainerPadding.dynamic0100,
								bottom: ContainerPadding.static100,
							}}
						>
							<CTA
								tag="button"
								emphasis="secondary"
								size="small"
								onClick={() => {
									openServicesLayer(ServiceType.LongDrive);
								}}
								aria-label="aria label for CTA"
								icon={<ChevronRight variant="small" aria-hidden />}
								iconPosition="end"
							>
								Saber más
							</CTA>
						</Container>
					</Container>
				)}
				{isWarrantyActive(vehicle) && (
					<Container
						wrap="always"
						padding={{
							top: ContainerPadding.dynamic0150,
							left: ContainerPadding.grid001,
							right: ContainerPadding.grid001,
						}}
					>
						<Text appearance={TokenTextAppearance.headline200} textAlign={TextAlignment.left} bold>
							Garantía de tu Volkswagen
						</Text>
						{warrantyDate !== null && (
							<ServiceStatusIndicator date={warrantyDate} fullDescription={true} diffDaysCheck={90} />
						)}
						<Text appearance={TokenTextAppearance.label200} textAlign={TextAlignment.left}>
							Con nuestra garantía de fabricante, tu coche está cubierto ante cualquier desperfecto de fabricación.
						</Text>
						<Container
							padding={{
								top: isMax959 ? ContainerPadding.dynamic0050 : ContainerPadding.dynamic0100,
								bottom: ContainerPadding.static100,
							}}
						>
							<CTA
								tag="button"
								emphasis="secondary"
								size="small"
								onClick={() => {
									openServicesLayer(ServiceType.GarantiaVolkswagen);
								}}
								aria-label="aria label for CTA"
								icon={<ChevronRight variant="small" aria-hidden />}
								iconPosition="end"
							>
								Saber más
							</CTA>
						</Container>
					</Container>
				)}
				{isIDcareActive(vehicle) && (
					<Container
						wrap="always"
						padding={{
							top: ContainerPadding.dynamic0150,
							left: ContainerPadding.grid001,
							right: ContainerPadding.grid001,
						}}
					>
						<Text appearance={TokenTextAppearance.headline200} textAlign={TextAlignment.left} bold>
							ID. Care
						</Text>

						<Container
							padding={{
								top: ContainerPadding.static150,
								bottom: ContainerPadding.static250,
							}}
							verticalAlign="center"
							shrinkContent
						>
							<DisplayTag appearance="new" text="Aprovéchalo" />
						</Container>

						<Text appearance={TokenTextAppearance.label200} textAlign={TextAlignment.left}>
							Si conduces un eléctrico, disfruta de una cobertura adicional de hasta 8 años ante averías eléctricas o
							mecánicas.
						</Text>
						<Container
							padding={{
								top: isMax959 ? ContainerPadding.dynamic0050 : ContainerPadding.dynamic0100,
								bottom: ContainerPadding.static100,
							}}
						>
							<CTA
								tag="button"
								emphasis="secondary"
								size="small"
								onClick={() => {
									openServicesLayer(ServiceType.IdCare);
								}}
								aria-label="aria label for CTA"
								icon={<ChevronRight variant="small" aria-hidden />}
								iconPosition="end"
							>
								Saber más
							</CTA>
						</Container>
					</Container>
				)}
			</Layout>
		</AccordionStyled>
	);
};
